import { Col, Modal, Row, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MATERIAL_ATTR_MAP, TOOLTIP_PLACEMENT } from '@common/constants';
import { getOnTrendLabelFromProduct } from '@common/helpers/onTrendLabel';
import { ColorModel } from '@common/models';
import ImageWithSkeleton from '@core/components/ImageWithSkeleton/ImageWithSkeleton';
import ImageUtils from '@core/utils/ImageUtils';

import './SlabViewer.scss';

export interface SlabViewerProps {
    visible: boolean;
    previewColor: ColorModel;
    materialName: string;
    onCancel: () => void;
}

export const MATERIAL_ATTRIBUTE = 'materialAttribute';

const SlabViewer = ({ visible, previewColor, materialName, onCancel }: SlabViewerProps): JSX.Element => {
    const { t } = useTranslation();
    const onTrendLabel = useMemo(() => {
        return getOnTrendLabelFromProduct(previewColor);
    }, [previewColor]);

    const getFeatureDescription = (name: string): string => {
        const attribute = MATERIAL_ATTR_MAP.get(name);
        return attribute ? t(`SCROLLABLE_AREA.COLOR.VIEW_SLAB.${attribute.textKey}`) : '';
    };

    const materialAttributes = previewColor.features.filter((feature) => feature.name === MATERIAL_ATTRIBUTE);
    const slabPreviewURL = previewColor.slabImageUrl.replace('-thumbnail', '');

    return (
        <Modal
            className="SlabViewer"
            open={visible}
            onCancel={onCancel}
            width="auto"
            centered
            footer={null}
            closable
            destroyOnClose // Prevent previous slab image cache
        >
            <h3 className="SlabViewer__title">{previewColor.name}</h3>
            <p className="SlabViewer__subtitle">{materialName}</p>
            <Row gutter={[20, 20]} wrap={false}>
                {materialAttributes.length ? (
                    <Col className="SlabViewer__features-container">
                        {materialAttributes.map((feature) => (
                            <Tooltip
                                key={feature.value}
                                placement={TOOLTIP_PLACEMENT}
                                overlayClassName="SlabViewer__tooltip"
                                title={
                                    <>
                                        <strong>{feature.value}</strong>
                                        {getFeatureDescription(feature.value)}
                                    </>
                                }
                                arrowPointAtCenter
                                color="#FFF"
                                className="SlabViewer__feature-icon"
                            >
                                <ImageWithSkeleton src={MATERIAL_ATTR_MAP.get(feature.value)?.img} />
                            </Tooltip>
                        ))}
                    </Col>
                ) : null}
                <Col>
                    <div className="SlabViewer__preview-container">
                        {onTrendLabel && <div className="SlabViewer__trend-badge">{onTrendLabel}</div>}
                        <ImageWithSkeleton
                            className="SlabViewer__preview-image"
                            src={ImageUtils.imgUrl(slabPreviewURL, 2400)}
                        />
                    </div>

                    <span className="SlabViewer__caption-text">
                        {t('SCROLLABLE_AREA.COLOR.VIEW_SLAB.INFO_MESSAGE', { material: materialName.toLowerCase() })}
                    </span>
                </Col>
            </Row>
        </Modal>
    );
};

export default SlabViewer;
